import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import FeatherIcon from 'feather-icons-react';
import { Spinner } from './Spinner';

export type ActionMenuVariant = 'default' | 'success' | 'danger' | 'warning' | 'primary';

export interface ActionMenuOption {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: ActionMenuVariant;
}

interface ActionMenuProps {
  icon?: string;
  iconSize?: number;
  ariaLabel?: string;
  onIconClick?: () => void;
  options: ActionMenuOption[];
  isLoading?: boolean;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  icon = 'more-horizontal',
  iconSize = 20,
  ariaLabel = 'More actions',
  onIconClick,
  options,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [menuPlacement, setMenuPlacement] = useState<'left' | 'right'>('left');
  const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const openToRight = rect.right < 188;
      setPosition({
        top: rect.bottom + 4,
        left: openToRight ? rect.left : rect.right,
      });
      setMenuPlacement(openToRight ? 'right' : 'left');
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsOpen(true);
    updatePosition();
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsOpen(false);
      setHoverTimeout(null);
    }, 250);
    setHoverTimeout(timeout);
  };

  const handleOptionClick = (option: ActionMenuOption) => {
    if (!option.disabled && !option.isLoading) {
      option.onClick();
      setIsOpen(false);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    }
  };

  const focusFirstEnabledMenuItem = () => {
    const firstEnabledIndex = options.findIndex(
      (option) => !option.disabled && !option.isLoading,
    );
    if (firstEnabledIndex >= 0) {
      requestAnimationFrame(() => menuItemRefs.current[firstEnabledIndex]?.focus());
    }
  };

  const handleIconClick = () => {
    if (onIconClick) {
      onIconClick();
      return;
    }
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      focusFirstEnabledMenuItem();
    }
  };

  const handleIconKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      focusFirstEnabledMenuItem();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <>
      <div
        style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={buttonRef}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          disabled={isLoading}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleIconClick}
          onKeyDown={handleIconKeyDown}
        >
          {isLoading ? (
            <Spinner size="sm" color="default" />
          ) : (
            <FeatherIcon icon={icon} size={iconSize} />
          )}
        </button>
      </div>
      
      {isOpen && createPortal(
        <div
          id={menuId}
          role="menu"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: menuPlacement === 'left' ? 'translateX(-100%)' : 'none',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10000,
            minWidth: 'min(150px, calc(100vw - 16px))',
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100vh - 16px)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            pointerEvents: 'auto'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setIsOpen(false);
              buttonRef.current?.focus();
              return;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const enabledIndexes = options.reduce<number[]>((indexes, option, index) => {
                if (!option.disabled && !option.isLoading) {
                  indexes.push(index);
                }
                return indexes;
              }, []);
              if (enabledIndexes.length === 0) return;

              const activeIndex = menuItemRefs.current.findIndex(
                (item) => item === document.activeElement,
              );
              const currentPosition = enabledIndexes.indexOf(activeIndex);
              const nextPosition =
                currentPosition === -1
                  ? event.key === 'ArrowDown'
                    ? 0
                    : enabledIndexes.length - 1
                  : event.key === 'ArrowDown'
                    ? (currentPosition + 1) % enabledIndexes.length
                    : (currentPosition - 1 + enabledIndexes.length) % enabledIndexes.length;
              menuItemRefs.current[enabledIndexes[nextPosition]]?.focus();
            }
          }}
        >
          {options.map((option, index) => {
            const variant = option.variant || 'default';
            
            // Define colors for each variant
            const variantStyles: Record<ActionMenuVariant, { backgroundColor: string; hoverBackgroundColor: string }> = {
              default: {
                backgroundColor: 'white',
                hoverBackgroundColor: '#f5f5f5'
              },
              success: {
                backgroundColor: '#e8f5e9',
                hoverBackgroundColor: '#c8e6c9'
              },
              danger: {
                backgroundColor: '#ffebee',
                hoverBackgroundColor: '#ffcdd2'
              },
              warning: {
                backgroundColor: '#fff3e0',
                hoverBackgroundColor: '#ffe0b2'
              },
              primary: {
                backgroundColor: '#fff5f3',
                hoverBackgroundColor: '#ffe5e0'
              }
            };
            
            const styles = variantStyles[variant];
            
            return (
              <button
                key={index}
                ref={(element) => {
                  menuItemRefs.current[index] = element;
                }}
                type="button"
                role="menuitem"
                style={{
                  padding: '8px 12px',
                  border: 'none',
                  background: styles.backgroundColor,
                  textAlign: 'left',
                  cursor: option.disabled || option.isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  opacity: option.disabled || option.isLoading ? 0.5 : 1,
                  transition: 'background-color 0.2s',
                  borderTop: index > 0 ? '1px solid #eee' : 'none'
                }}
                disabled={option.disabled || option.isLoading}
                onMouseEnter={(e) => {
                  if (!option.disabled && !option.isLoading) {
                    e.currentTarget.style.backgroundColor = styles.hoverBackgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.backgroundColor;
                }}
                onClick={() => handleOptionClick(option)}
              >
                {option.isLoading ? `${option.label}...` : option.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
};

